import { gql } from 'apollo-boost';

import { bidModifiersFragment } from './bidModifiers';
import { campaignStrategyBudgetFragment } from './domain';
import { allowedBidModifiersValuesFragment } from './rateCorrections';

export const vCardCampaignDataFragment = gql`
    fragment VCardCampaignDataFragment on GdCampaignVcard {
        id
        campaignId
        companyName
        contactPerson
        extraMessage
        email
        ogrn

        instantMessenger {
            type
            login
        }

        workTimes {
            daysOfWeek
            startTime {
                hour
                minute
            }
            endTime {
                hour
                minute
            }
        }

        phone {
            countryCode
            cityCode
            phoneNumber
            extension
        }

        address {
            geoId
            country
            city
            street
            houseWithBuilding
            apartment
            metroStationId
            metroStationName
            pointPrecision
            pointOnMap {
                x
                y
                x1
                y1
                x2
                y2
            }
        }
    }
`;

export const campaignsNotificationFragment = gql`
    fragment campaignsNotificationFragment on GdCampaignNotification {
        emailSettings {
            allowedEvents
            checkPositionInterval
            email
            sendAccountNews
            stopByReachDailyBudget
            warningBalance
            xlsReady
        }
        smsSettings {
            smsTime {
                endTime {
                    hour
                    minute
                }
                startTime {
                    hour
                    minute
                }
            }
            events {
                checked
                event
            }
        }
    }
`;

const campaignVerboseStrategyFragment = gql`
    fragment campaignVerboseStrategyFragment on GdCampaignFlatStrategy {
        type
        isAutoBudget
        platform
        budget {
            ...campaignStrategyBudgetFragment
        }
        ... on GdCampaignStrategyManual {
            separateBidding
        }
        ... on GdCampaignStrategyAvgClick {
            avgBid
        }
        ... on GdCampaignStrategyAvgCpa {
            goalId
            avgCpa
            bid
        }
        ... on GdCampaignStrategyRoi {
            goalId
            profitability
            reserveReturn
            roiCoef
            bid
        }
        ... on GdCampaignStrategyWeekBudget {
            bid
            goalId
        }
        ... on GdCampaignStrategyWeekBundle {
            avgBid
            bid
            clicksLimit
        }
    }

    ${campaignStrategyBudgetFragment}
`;

export const campaignsStatusFragment = gql`
    fragment campaignsStatusFragment on GdCampaignStatus {
        allowDomainMonitoring
    }
`;

export const textCampaignFragment = gql`
    fragment TextCampaignFragment on GdTextCampaign {
        contentLanguage
        disabledPlaces
        meaningfulGoals {
            goalId
            conversionValue
        }
        metrikaCounters
        hasTitleSubstitution
        clientDialogId
        hasExtendedGeoTargeting
        allowedPageIds
        hasAddMetrikaTagToUrl
        hasAddOpenstatTagToUrl
        excludePausedCompetingAds
        enableCompanyInfo
        broadMatch {
            broadMatchFlag
            broadMatchGoalId
            broadMatchLimit
        }
        commonVcard {
            ...VCardCampaignDataFragment
        }
    }
    ${vCardCampaignDataFragment}
`;

export const campaignsEditDataQuery = gql`
    query CampaignsEditDataQuery(
        $login: String!
        $campaignInput: GdCampaignsContainerInput!
        $broadMatchGoalsInput: GdBroadMatchesContainerInput!
        $city: [GdMetroCityInput!]
    ) {
        reqId: getReqId
        operator {
            access {
                availableFields {
                    campaignFields
                }
            }
        }
        client(searchBy: { login: $login }) {
            campaigns(input: $campaignInput) {
                rowset {
                    id
                    exportId
                    name
                    type
                    flatStrategy {
                        ...campaignVerboseStrategyFragment
                    }
                    startDate
                    endDate
                    bidModifiers {
                        ...bidModifiersFragment
                    }
                    disabledIps
                    notification {
                        ...campaignsNotificationFragment
                    }
                    status {
                        ...campaignsStatusFragment
                    }
                    ...TextCampaignFragment
                }
            }
            notification {
                smsPhone
            }
            clientConstants {
                allowedBidModifierTypesByCampaignTypes {
                    campaignType
                    allowedBidModifierTypes
                }
                allowedBidModifiersValues {
                    ...allowedBidModifiersValuesFragment
                }
            }
            broadMatchGoals(input: $broadMatchGoalsInput) {
                campaignGoals {
                    campaignId
                    goals {
                        id
                        name
                    }
                }
                isMetrikaAvailable
            }
        }
        constants {
            defaultValues {
                campaign {
                    defaultMeaningfulGoalId
                }
            }
            validation {
                campaignConstants {
                    maxDisabledPlacesCount
                    maxMetrikaCountersNumberForTextCampaign
                    maxDisabledIpsCount
                    minBroadMatchLimit
                    maxBroadMatchLimit
                    maxNameLength
                }
            }
            defaultValues {
                campaign {
                    defaultBroadMatchAllGoalsId
                }
            }
            metroStations(input: { filter: { metroCityIn: $city } }) {
                rowset {
                    geoRegionId
                    metroStationId
                    metroStationName
                }
            }
        }
    }
    ${allowedBidModifiersValuesFragment}
    ${campaignVerboseStrategyFragment}
    ${campaignsNotificationFragment}
    ${campaignsStatusFragment}
    ${textCampaignFragment}
    ${bidModifiersFragment}
`;

export const campaignsAvailableForKeyGoalsQuery = gql`
    query CampaignsAvailableForKeyGoalsQuery(
        $login: String!
        $availableForKeyGoalsInput: GdMeaningfulGoalsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            meaningfulGoals(input: $availableForKeyGoalsInput) {
                isMetrikaAvailable
                campaignGoals {
                    campaignId
                    goals {
                        id
                        name
                    }
                }
            }
        }
    }
`;
